import { FunctionComponent, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TABLE_CELL_MIN_WIDTH, TableBlockData, useLinkify } from '../../../../shared';

import classNames from 'classnames';
import classes from './Table.module.scss';

export const Table: FunctionComponent<TableBlockData> = memo(
  ({ withHeadings, content, columnWidths }) => {
    const { t } = useTranslation();

    const { linkify } = useLinkify();

    const [isScrollable, setIsScrollable] = useState(false);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const tableRef = useRef<HTMLTableElement>(null);

    useEffect(() => {
      const wrapperNode = wrapperRef.current;

      const tableNode = tableRef.current;

      if (!wrapperNode || !tableNode) {
        return;
      }

      const resizeObserver = new ResizeObserver(() =>
        setIsScrollable(wrapperNode.scrollWidth > wrapperNode.clientWidth)
      );

      resizeObserver.observe(wrapperNode);
      resizeObserver.observe(tableNode);

      return () => resizeObserver.disconnect();
    }, []);

    const widths = useMemo(
      () => (content[0] ?? []).map((_, index) => columnWidths[index] ?? null),
      [columnWidths, content]
    );

    const hasColumnWidths = widths.some((width) => width !== null);

    const tableStyle = useMemo(() => {
      const totalWidth = widths.reduce<number>(
        (total, width) => total + (width ?? TABLE_CELL_MIN_WIDTH),
        0
      );

      if (widths.every((width) => width !== null)) {
        return { width: totalWidth };
      }

      return { minWidth: totalWidth };
    }, [widths]);

    const renderColumnGroup = useMemo(() => {
      if (!hasColumnWidths) {
        return null;
      }

      return (
        <colgroup>
          {widths.map((width, index) => (
            <col key={`col${index}`} {...(width !== null && { style: { width } })} />
          ))}
        </colgroup>
      );
    }, [hasColumnWidths, widths]);

    const renderHeader = useMemo(() => {
      if (!withHeadings) {
        return null;
      }

      return (
        <thead>
          <tr>
            {content[0].map((header, index) => (
              <th key={`th${index}`} dangerouslySetInnerHTML={{ __html: linkify(header) }}></th>
            ))}
          </tr>
        </thead>
      );
    }, [content, linkify, withHeadings]);

    const renderBody = useMemo(() => {
      return (
        <tbody>
          {content.map((row, index) => {
            if (withHeadings && !index) {
              return false;
            }

            return (
              <tr key={`tr${index}`}>
                {row.map((cell, index) => {
                  return (
                    <td
                      key={`td${index}`}
                      dangerouslySetInnerHTML={{
                        __html: linkify(cell),
                      }}
                    ></td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      );
    }, [content, linkify, withHeadings]);

    const table = (
      <table
        ref={tableRef}
        className={classNames(classes['table'], {
          [classes['table--column-widths']]: hasColumnWidths,
        })}
        {...(hasColumnWidths && { style: tableStyle })}
      >
        {renderColumnGroup}
        {renderHeader}
        {renderBody}
      </table>
    );

    if (!isScrollable) {
      return (
        <div ref={wrapperRef} className={classes['wrapper']}>
          {table}
        </div>
      );
    }

    return (
      <div
        ref={wrapperRef}
        className={classes['wrapper']}
        role={'region'}
        tabIndex={0}
        aria-label={t('richTextEditor.tool-names-table')}
      >
        {table}
      </div>
    );
  }
);
